import React from 'react';
import { motion } from 'framer-motion';

const Hero: React.FC = () => {
  // Particle burst effect for background
  const particles = Array.from({ length: 15 }).map((_, i) => (
    <motion.div
      key={i}
      className="absolute w-2 h-2 bg-gold-300/50 rounded-full blur-sm"
      initial={{
        x: Math.random() * 1000 - 500,
        y: Math.random() * 600 - 300,
        opacity: 0,
        scale: 0
      }}
      animate={{
        x: Math.random() * 1000 - 500,
        y: Math.random() * 600 - 300,
        opacity: [0, 1, 0],
        scale: [0, 1.5, 0]
      }}
      transition={{
        duration: Math.random() * 5 + 3,
        repeat: Infinity,
        delay: Math.random() * 2
      }}
    />
  ));

  // Rotating accent rings
  const rings = Array.from({ length: 3 }).map((_, i) => (
    <motion.div
      key={i}
      className="absolute w-64 h-64 border border-gold-400/20 rounded-full"
      style={{ 
        top: `${40 + i * 10}%`, 
        left: `${50 + i * 5}%`, 
        transform: 'translate(-50%, -50%)' 
      }}
      initial={{ rotate: 0, scale: 0.8, opacity: 0.3 }}
      animate={{ rotate: 360, scale: [0.8, 1.2, 0.8], opacity: [0.3, 0.6, 0.3] }}
      transition={{ 
        duration: 10 + i * 2, 
        repeat: Infinity, 
        ease: 'linear' 
      }}
    />
  ));

  return (
    <section className="relative bg-black overflow-hidden min-h-screen flex items-center justify-center">
      {/* Animated background gradient */}
      <motion.div 
        className="absolute inset-0 bg-gradient-to-r from-gold-900/20 via-gold-800/30 to-gold-900/20"
        animate={{ 
          backgroundPosition: ['0% center', '100% center', '0% center'],
        }}
        transition={{ 
          duration: 10, 
          repeat: Infinity, 
          ease: 'linear' 
        }}
        style={{ 
          backgroundSize: '200% auto',
        }}
      />
      
      {/* Particle burst effects */}
      {particles}
      
      {/* Floating orb elements for lively movement */}
      <motion.div 
        className="absolute top-10 left-10 w-24 h-24 bg-gold-500/10 rounded-full blur-xl"
        animate={{ 
          x: [0, 20, -20, 0],
          y: [0, -30, 30, 0],
        }}
        transition={{ 
          duration: 10,
          repeat: Infinity, 
          ease: 'easeInOut'
        }}
      />
      <motion.div 
        className="absolute top-20 right-10 w-32 h-32 bg-gold-400/8 rounded-full blur-xl"
        animate={{ 
          x: [0, -25, 25, 0],
          y: [0, 20, -20, 0],
        }}
        transition={{ 
          duration: 12,
          repeat: Infinity, 
          ease: 'easeInOut'
        }}
      />
      <motion.div 
        className="absolute bottom-20 left-1/3 w-16 h-16 bg-gold-300/10 rounded-full blur-lg"
        animate={{ 
          x: [0, 15, -15, 0],
          y: [0, -15, 15, 0],
        }}
        transition={{ 
          duration: 8,
          repeat: Infinity, 
          ease: 'easeInOut'
        }}
      />
      
      {/* Rotating accent rings */}
      {rings}
      
      {/* Content */}
      <div className="relative max-w-7xl mx-auto px-4 py-24 sm:py-32 sm:px-6 lg:px-8 z-10">
        <div className="md:w-2/3">
          <motion.div
            className="inline-flex items-center gap-2 rounded-full border border-gold-700/40 bg-gold-400/10 px-4 py-1.5 mb-6 text-sm font-medium text-gold-300"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease: 'easeOut' }}
          >
            <span className="w-2 h-2 rounded-full bg-gold-400 animate-pulse" />
            Custom software studio for small business
          </motion.div>
          <motion.h1
            className="text-4xl font-bold tracking-tight text-gold-400 sm:text-5xl lg:text-6xl"
            initial={{ opacity: 0, y: 30, scale: 0.8 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            transition={{ duration: 1.2, ease: 'easeOut', type: 'spring', stiffness: 100 }}
          >
            Custom Software & AI, Built for Small Business
          </motion.h1>
          <motion.p
            className="mt-6 text-xl text-gray-300 max-w-3xl"
            initial={{ opacity: 0, y: 20, x: -50 }}
            animate={{ opacity: 1, y: 0, x: 0 }}
            transition={{ duration: 0.8, delay: 0.2, ease: 'easeOut' }}
          >
            Setora designs and ships the web apps, mobile apps, and AI-powered tools that growing businesses run on — from field-service estimating and booking platforms to AI assistants that turn your data into decisions.
          </motion.p>
          <div className="mt-10 flex flex-col sm:flex-row gap-4">
            <motion.a
              href="/contact"
              className="inline-flex justify-center items-center rounded-2xl bg-gold-400 px-5 py-3 text-base font-medium text-black hover:bg-gold-300 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gold-400 transition-all duration-300 shadow-lg shadow-gold-500/30 pulse-glow"
              initial={{ opacity: 0, y: 20, scale: 0.5, rotate: -10 }}
              animate={{ opacity: 1, y: 0, scale: 1, rotate: 0 }}
              transition={{ duration: 1, delay: 0.4, ease: 'easeOut', type: 'spring', stiffness: 150 }}
              whileHover={{ scale: 1.05, boxShadow: '0 10px 25px -5px rgba(211, 158, 61, 0.5)', rotate: 2 }}
              whileTap={{ scale: 0.95, rotate: -2 }}
            >
              Start a Project
            </motion.a>
            <motion.a
              href="/#services"
              className="inline-flex justify-center items-center rounded-2xl border border-gold-400 px-5 py-3 text-base font-medium text-gold-400 hover:bg-gold-400/10 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gold-400 transition-all duration-300"
              initial={{ opacity: 0, y: 20, scale: 0.5, rotate: 10 }}
              animate={{ opacity: 1, y: 0, scale: 1, rotate: 0 }}
              transition={{ duration: 1, delay: 0.6, ease: 'easeOut', type: 'spring', stiffness: 150 }}
              whileHover={{ scale: 1.05, rotate: -2 }}
              whileTap={{ scale: 0.95, rotate: 2 }}
            >
              Explore Services
            </motion.a>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero; 